import type { ExecutivePower, Faction, PolicyType, WinReason } from './enums';

/*
 * A theme (game variant) supplies every player-facing word; the mechanics
 * are shared. Engine codes are fixed mechanical slots:
 *
 *   Faction.SEPARATIST   the uninformed majority
 *   Faction.FEDERALIST   the informed minority
 *   SpecialRole.NENSHI   the minority's hidden leader
 *   PolicyFaction.*      each faction's policy type
 *
 * Themes name those slots however they like. Strings may contain
 * {placeholders}, filled in by the client.
 */

export type ThemeId = string;
export type ThemeStatus = 'released' | 'preview';

export interface FactionTerms {
  /** "Separatist" */
  name: string;
  /** "Separatists" */
  plural: string;
  /** End-game card colours. */
  accent: string;
  accentInk: string;
}

export interface PolicyTerms {
  /** "Separation Policy" */
  name: string;
  /** "Separation", as in "2 Separation and 1 Federalist" */
  short: string;
  /** Labels for each space on this policy's track. */
  track: string[];
  /** Flavour headlines printed on cards of this type. */
  headlines: string[];
}

export interface PowerTerms {
  /** "Campaign Research" */
  name: string;
  /** Short label for the track, e.g. "Research". */
  short: string;
  narrator: string;
}

export interface WinTerms {
  headline: string;
  winner: string;
  line?: string;
}

export interface ThemeText {
  title: string;
  /** "Alberta Edition" */
  editionName: string;
  tagline: string;
  /** One or two sentences for the game-type menu. */
  menuDescription: string;
  disclaimer: { lead: string; body: string };
  about: string[];
  /** How to Play story. Items starting with "!" are shown as emphasised lines. */
  opening: string[];
  openingHeading: string;

  factions: Record<Faction, FactionTerms>;
  leader: {
    /** "Nenshi": used in knowledge text and the identity reveal. */
    name: string;
    /** "Secret Nenshi": the card stamp and reveal title. */
    title: string;
    accent: string;
    accentInk: string;
    /** Shown on the leader's card when elected with enough policies. */
    electedKicker: string;
    momentumTitle: string;
    momentumText: string;
  };
  offices: { minister: string; premier: string };
  policies: Record<PolicyType, PolicyTerms>;
  powers: Record<Exclude<ExecutivePower, 'NONE' | 'FEDERALIST_VICTORY' | 'COURT_CHALLENGE_AND_VETO'>, PowerTerms> & {
    VETO: PowerTerms;
  };

  phases: {
    confidenceVote: { name: string; confidence: string; noConfidence: string; narrator: string; after: string };
    ministerBriefing: string;
    premierDesk: string;
    policyEnacted: { name: string; narrator: string };
    gridlock: { name: string; short: string; title: string; narrator: string; automatic: string };
    speech: { name: string; narrator: string };
    questionPeriod: { name: string; narrator: string };
    waiting: string;
    removed: { title: string; narrator: string };
  };

  wins: Record<WinReason, WinTerms>;
  gameOver: { truth: string; intro: string; cabinet: string; fiction: string };
  lobby: {
    /** "{count} Albertans have gathered to decide the future of four million others." */
    gathered: string;
    quorum: string;
  };
}

export interface ThemeSummary {
  id: ThemeId;
  title: string;
  editionName: string;
  tagline: string;
  menuDescription: string;
  status: ThemeStatus;
}

/**
 * The words an edition's character files use for the mechanical slots, e.g.
 * { majority: 'GOVERNMENT', minority: 'CONSERVATIVE', leader: 'POILIEVRE' }.
 */
export interface EditionKeys {
  majority: string;
  minority: string;
  leader: string;
}

export const DEFAULT_THEME_ID: ThemeId = 'secret-nenshi';
